import { useCallback, useEffect, useMemo, useRef } from 'react';
import { sendKeptAlive } from '../../../Api/callExternalApi';

const DEBOUNCE_MS = 1000;

type SaveResult = { error: { message: string } | null };
type Save = () => Promise<SaveResult>;

interface PendingSave {
  timer?: NodeJS.Timeout;
  run: () => Promise<boolean>;
}

// Leaving the page sends what is still waiting, and a failed save stays queued until a newer edit replaces it.
export const usePendingSaves = (enabled: boolean, onError: (message: string) => void) => {
  const pending = useRef<Record<string, PendingSave>>({});
  const inFlight = useRef(new Set<Promise<boolean>>());
  const sending = useRef<Record<string, Promise<boolean>>>({});
  const latest = useRef<Record<string, number>>({});
  // The newest edit for each key until it saves, so closing the tab can still send it.
  const unsaved = useRef<Record<string, Save>>({});
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  // Only refs inside, so both stay the same across renders.
  const { track, runNow } = useMemo(() => {
    const track = (key: string, save: Save, request: Promise<SaveResult>): Promise<boolean> => {
      const started = (latest.current[key] ?? 0) + 1;
      latest.current[key] = started;
      const saving = request.then(({ error }) => {
        // Each save sends the whole field, so a newer save for the key decides and an older one is never re-sent.
        if (latest.current[key] !== started) return true;
        if (!error) {
          if (unsaved.current[key] === save) delete unsaved.current[key];
          return true;
        }
        onErrorRef.current(error.message);
        if (!pending.current[key]) pending.current[key] = { run: () => runNow(key, save) };
        return false;
      });
      sending.current[key] = saving;
      inFlight.current.add(saving);
      saving.then(() => {
        inFlight.current.delete(saving);
        if (sending.current[key] === saving) delete sending.current[key];
      });
      return saving;
    };

    // One request per key at a time, so an older write can never reach the server after a newer one.
    const runNow = (key: string, save: Save): Promise<boolean> => {
      delete pending.current[key];
      const previous = sending.current[key];
      return track(key, save, previous ? previous.then(() => save()) : save());
    };

    return { track, runNow };
  }, []);

  const sendPending = useCallback(() => {
    Object.values(pending.current).forEach(({ timer, run }) => {
      clearTimeout(timer);
      run();
    });
  }, []);

  useEffect(() => sendPending, [sendPending]);

  // Closing the tab unmounts nothing and can cancel ordinary requests, so the newest edits go out kept alive.
  useEffect(() => {
    const holdTheTab = (event: BeforeUnloadEvent) => {
      const keys = Object.keys(unsaved.current);
      if (keys.length === 0) return;
      sendKeptAlive(() =>
        keys.forEach((key) => {
          clearTimeout(pending.current[key]?.timer);
          delete pending.current[key];
          const save = unsaved.current[key];
          // Waiting behind a request still sending would miss the keepalive window, so an older answer can land after
          // this one; that rare reorder beats losing the newest edit when the tab closes.
          track(key, save, save());
        })
      );
      event.preventDefault();
    };
    window.addEventListener('beforeunload', holdTheTab);
    return () => window.removeEventListener('beforeunload', holdTheTab);
  }, [track]);

  const debouncedSave = useCallback(
    (key: string, save: Save) => {
      if (!enabled) return;
      clearTimeout(pending.current[key]?.timer);
      unsaved.current[key] = save;
      const run = () => runNow(key, save);
      pending.current[key] = { timer: setTimeout(run, DEBOUNCE_MS), run };
    },
    [enabled, runNow]
  );

  // Resolves true once every edit, waiting or already sending, has saved.
  const flushSaves = useCallback(async () => {
    sendPending();
    const results = await Promise.all([...inFlight.current]);
    return results.every(Boolean);
  }, [sendPending]);

  return { debouncedSave, flushSaves };
};
